<template>
  <div class="sf-grid sf-grid-fluid sf-grid-gutter-none" :class="outerClass">
    <div
      v-if="compareMode && !isEmpty"
      class="
        sf-grid sf-grid-fluid sf-grid-gutter-medium sf-layer-border-bottom-dark
      "
    >
      <div
        class="
          sf-row
          sf-row-nowrap
          sf-divide-h-solid
          sf-divide-snow-darker
          sf-below-0
          sf-pad-h-3
        "
        :class="hideSessionClass"
      >
        <div class="sf-col sf-md-6 sf-level sf-pad-v-2">
          <span class="sf-pad-r-1">{{
            I18n.t('workflow_review.current_period')
          }}</span>
          <span class="sf-bold">{{ ledgerLabel }}</span>
        </div>
        <div class="sf-col sf-md-6 sf-level sf-pad-v-2">
          <div v-if="!compareLedgerId">
            {{ I18n.t('workflow_review.nothing_to_compare') }}
          </div>
          <div v-else class="sf-level">
            <span class="sf-pad-r-1">{{
              I18n.t('workflow_review.previous_period')
            }}</span>
            <div ref="periodPickerDropdown" class="sf-dropdown">
              <div v-if="reloadingLedger" class="sf-spinner" />
              <div v-else class="sf-form-select-wrapper" data-toggle="dropdown">
                <div class="sf-form-select">
                  {{ compareLedgerLabel }}
                </div>
              </div>
              <period-picker
                class="sf-dropdown-menu"
                :current-ledger-id="compareLedgerId"
                :years="periodPickerYears"
                :ledgers-per-year="comparePeriodPickerData.ledgers_per_year"
                @change-compare-period="periodPicked"
              />
            </div>
          </div>
          <div
            class="dropdown-menu dropdown-menu-periodpicker"
            data-behaviour="periodpicker set-current-ledger"
          />
        </div>
      </div>
    </div>
    <div v-if="isEmpty" class="sf-col sf-pad-3 sf-alignc">
      <div class="workflow-review_empty-state-container">
        <div class="sf-empty-state">
          <img
            class="sf-empty-state-image"
            :src="$imagesRequire('empty_states/client-file.svg')"
          />
          <div class="sf-empty-state-title">
            {{ I18n.t('workflow_review.empty_state.title') }}
          </div>
          <div class="sf-empty-state-text sf-x-text">
            <div
              v-if="filter !== null"
              v-dompurify-html="
                I18n.t('workflow_review.empty_state.message_filter_html')
              "
              @click.prevent.stop="clearFilter"
            />
            <div v-else>
              {{ I18n.t('workflow_review.empty_state.message') }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <scrollable-content
      v-else
      :current-index="currentIndex"
      @item-focused="handleNewItemFocus"
    >
      <template #default="{ observer, getScrollableRef }">
        <div class="tw-pb-[70vh]">
          <div data-walkthrough-id="workflow-review-list">
            <workflow-review-item-list
              :items="items"
              :context-id="contextId"
              :total="itemLength"
              :current-index="currentIndex"
              :next-item-index-to-focus="nextReviewItemIndexToFocus"
              :observer="observer"
              :get-scrollable-ref="getScrollableRef"
              :fetching-communications-status="fetchingCommunicationsStatus"
            />
          </div>
        </div>
      </template>
    </scrollable-content>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { mapActions as mapPiniaActions, mapState as mapPiniaState } from 'pinia'
import humps from 'humps'
import debounce from 'lodash/debounce'
import { v4 as generateUuid } from 'uuid'

import WorkflowReviewItemList from './workflow-review-item-list'
import WorkflowReviewSidepane from './workflow-review-sidepane'
import ScrollableContent from './scrollable-content'
import PeriodPicker from '@app/application/layout/components/period-picker'
import Polling from '@app/utils/polling'
import { setQueryParam } from '@app/utils/url-helpers'
import { createBatchesMiddleOut } from '@app/utils/batch-helpers'
import { useComparisonModeToggle } from '@app/application/workflows/composables/use-comparison-mode-toggle'
import { usePersistFocusedItem } from '@app/application/workflows/composables/use-persist-focused-item'

import { useAuditsStore } from '@app/application/sidebar-audit-log/store/audits'
import { useChecksStore } from '@app/application/sf-assistant-checks/stores/checks'
import { useContext } from '@app/shared/pinia/context'
import { useDrawer } from '@app/shared/pinia/drawer'
import { useWorkflowReviewFilter } from '@app/application/workflows/screens/workflow-review/store/pinia/workflow-review-filter'
import { useWorkflowReviewFocus } from '../store/pinia/workflow-review-focus'
import { useWorfklowReviewItems } from '../store/pinia/workflow-review-items'
import { useWorkflowReviewItemStatus } from '../store/pinia/workflow-review-item-status'
import { useWorfklowUI } from '../store/pinia/workflow-ui'

import api from '../api'
import useRedactSessionRecording from '@app/shared/vue/composables/use-hide-session-recording'

const WorkflowReview = {
  name: 'WorkflowReview',
  components: {
    WorkflowReviewItemList,
    WorkflowReviewSidepane,
    ScrollableContent,
    PeriodPicker,
  },
  setup(props) {
    const { turnOnCompareMode } = useComparisonModeToggle(false)
    const hideSessionClass = useRedactSessionRecording()

    usePersistFocusedItem({
      ledgerId: props.ledgerId,
      workflowId: props.workflowId,
    })

    return {
      turnOnCompareMode,
      hideSessionClass,
    }
  },
  props: {
    workflowId: {
      type: [String, Number],
      required: true,
    },
    ledgerId: {
      type: [String, Number],
      required: true,
    },
    ledgerLabel: {
      type: String,
      required: true,
    },
    compareLedgerData: {
      type: Object,
      required: true,
    },
    initialCompareMode: {
      type: Boolean,
      required: true,
    },
    initialItemsRange: {
      type: Object,
      required: true,
    },
    initialReviewItems: {
      type: Array,
      required: false,
      default: () => [],
    },
    batchSize: {
      type: Number,
      required: true,
    },
    messageBusChannelName: {
      type: String,
      required: false,
      default: null,
    },
    messageBusLastId: {
      type: String,
      required: false,
      default: null,
    },
    sidepaneFixed: {
      type: Boolean,
      required: false,
      default: false,
    },
    useAssistantChecks: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      requestRef: generateUuid(),
      compareLedgerLabel: this.compareLedgerData.compare_ledger_label,
      comparePeriodPickerData:
        this.compareLedgerData.compare_period_picker_data,
    }
  },
  computed: {
    ...mapState({
      contextId: (state) => state.contextId,
      contextType: (state) => state.contextType,
      fetchingCommunicationsStatus: (state) =>
        state.ui.fetchingCommunicationsStatus,
    }),
    ...mapPiniaState(useDrawer, ['isAnyDrawerOpen']),
    ...mapPiniaState(useWorkflowReviewFilter, ['filter']),
    ...mapPiniaState(useWorkflowReviewFocus, ['nextReviewItemIndexToFocus']),
    ...mapPiniaState(useWorfklowReviewItems, [
      'currentIndex',
      'currentReviewItem',
      'isEmpty',
      'itemLength',
      'reviewItems',
    ]),
    ...mapPiniaState(useWorfklowUI, [
      'compareLedgerId',
      'compareMode',
      'reloadingLedger',
    ]),
    actionBarClass() {
      return {
        'sf-pad-h-3': !this.isEmpty,
        'workflow-review_action-bar--empty': this.isEmpty,
      }
    },
    batches() {
      const indexes = Array.from(Array(this.items.length).keys())

      return createBatchesMiddleOut(
        indexes,
        this.initialItemsRange.bottom,
        this.initialItemsRange.top,
        this.batchSize
      )
    },
    items() {
      return this.reviewItems.map((item, index) => ({ ...item, index }))
    },
    outerClass() {
      return this.compareMode && this.isAnyDrawerOpen
        ? 'workflow-review--narrow'
        : ''
    },
    periodPickerYears() {
      return this.comparePeriodPickerData.years.map((year) =>
        humps.camelizeKeys(year)
      )
    },
  },
  watch: {
    currentReviewItem(newValue, oldValue) {
      if (
        newValue &&
        (newValue.id !== oldValue?.id ||
          newValue.itemClass !== oldValue?.itemClass)
      ) {
        this.setContextName(newValue.itemName)
      }
    },
    nextReviewItemIndexToFocus(newValue, oldValue) {
      if (
        newValue !== oldValue &&
        newValue !== null &&
        newValue !== this.currentIndex
      ) {
        this.handleNewItemFocus(newValue)
      }
    },
  },
  created() {
    this.receiveReviewItems(this.initialReviewItems)
    this.receiveItemStatuses(this.initialReviewItems)
    this.setCompareLedgerId(this.compareLedgerData.compare_ledger_id)

    this.debounceSetCommunicationContext = debounce(
      this.debouncedSetCommunicationContext,
      500
    )

    // TO-DO: Only keep this one when we handle context outside of the comms store
    this.setContext(this.contextType, this.contextId)

    this.fetchChangesSinceLastVisit({
      contextId: this.contextId,
      contextType: this.contextType,
    })

    this.updateCompareMode(this.initialCompareMode)

    window.eventHub.$on(
      'refreshCommunicationData',
      this.onRefreshCommunicationData
    )

    this.fetchBatchItems()

    this.fetchAssistantChecks(this.currentIndex)

    this.subscribeToLiquidUpdates()
  },
  async beforeUnmount() {
    window.eventHub.$off(
      'refreshCommunicationData',
      this.onRefreshCommunicationData
    )
    if (this.polling) {
      this.polling.unsubscribe()
    }

    await api.cancelWorkflowReviewJobs({
      ledgerId: this.ledgerId,
      workflowId: this.workflowId,
      requestRef: this.requestRef,
    })
  },
  methods: {
    ...mapActions(['setCommunicationContext', 'fetchTagCloud']),
    ...mapPiniaActions(useAuditsStore, ['fetchChangesSinceLastVisit']),
    ...mapPiniaActions(useContext, ['setContext', 'setContextName']),
    ...mapPiniaActions(useWorkflowReviewFilter, ['applyFilter']),
    ...mapPiniaActions(useWorfklowReviewItems, [
      'receiveReviewItems',
      'updateReviewItem',
      'updateReviewItemHTML',
      'updateCurrentReviewItemIndex',
    ]),
    ...mapPiniaActions(useWorkflowReviewItemStatus, [
      'receiveItemStatuses',
      'updateReviewItemStatus',
    ]),
    ...mapPiniaActions(useWorfklowUI, [
      'setCompareLedgerId',
      'toggleReloadingLedger',
      'toggleReloadingMode',
      'updateCompareMode',
    ]),
    async clearFilter() {
      await this.applyFilter('show-all')

      Turbolinks.visit(window.location.href)
    },
    ...mapPiniaActions(useChecksStore, ['resetChecks']),
    async periodPicked(compareLedgerId) {
      this.$refs.periodPickerDropdown.classList.remove('open')
      this.toggleReloadingLedger()
      await this.storePickedPeriod(compareLedgerId)
      Turbolinks.visit(window.location.href)
    },
    storePickedPeriod(compareLedgerId) {
      this.turnOnCompareMode({
        ledgerId: this.ledgerId,
        workflowId: this.workflowId,
        compareLedgerId,
      })
    },
    subscribeToLiquidUpdates() {
      const channelName = this.messageBusChannelName
      const lastId = this.messageBusLastId

      this.polling = new Polling(channelName, parseInt(lastId), (response) => {
        const requestRef = response.request_ref

        if (requestRef !== this.requestRef) {
          return
        }

        setTimeout(
          () =>
            response.items.forEach((item) => this.updateReviewItemHTML(item)),
          0
        )
      })

      this.polling.subscribe()
    },
    fetchBatchItems(idx = 0) {
      if (this.batches.length === 0) return

      const batchIndexes = this.batches[idx]
      const items = batchIndexes
        .map((i) => this.items[i])
        .map((item) => [item.id, item.itemClass])
      // compareLedgerId = can be the default compare mode, but we only want to request
      // the templates with compareLedgerId if we actually are in compare mode
      const compareLedgerId = this.compareMode ? this.compareLedgerId : null

      api
        .getBatch({
          items,
          workflowId: this.workflowId,
          ledgerId: this.ledgerId,
          compareLedgerId,
          requestRef: this.requestRef,
        })
        .then((data) => {
          if (!data) {
            return
          }
          // replace elements one by one to allow Vue companent to calc each element size change for smooth scrolling
          for (let i = 0; i < data.length; i++) {
            // replacing an element in a loop directly makes scroll position jump because of the recalculation timing
            // therefore do in within setTimeout, so JS handles one element in a separate tick
            const item = data[i]

            this.updateReviewItemStatus({
              parentId: item?.id,
              ...(item?.workflowReviewStatusData || {}),
            })

            setTimeout(() => {
              this.updateReviewItem({ item })
            }, 0)
          }

          if (idx < this.batches.length - 1) {
            this.fetchBatchItems(idx + 1)
          }
        })
    },
    handleNewItemFocus(newIndex) {
      if (this.currentIndex === newIndex) {
        return
      }

      this.updateCurrentReviewItemIndex(newIndex)

      this.updateUrl()
      this.debounceSetCommunicationContext(newIndex)
    },
    updateUrl() {
      const item = this.currentReviewItem

      setQueryParam(item.itemClass, item.id, '')
    },
    debouncedSetCommunicationContext(newIndex) {
      const { contextType, contextId, contextHrefParam } = this.items[newIndex]

      this.setCommunicationContext({ contextType, contextId, contextHrefParam })
      this.fetchAssistantChecks(newIndex)
    },
    fetchAssistantChecks(index) {
      if (!this.useAssistantChecks) {
        return
      }

      if (this.items[index]) {
        const { contextType, contextId } = this.items[index]
        this.resetChecks(contextType, contextId)
      }
    },
    onRefreshCommunicationData() {
      this.fetchTagCloud({
        workflowId: this.workflowId,
        ledgerId: this.ledgerId,
      })
    },
  },
}

export default WorkflowReview
</script>

<style lang="scss" scoped>
.workflow-review {
  &--narrow {
    max-width: calc(100vw - var(--sidebar-right-width));
  }

  &_empty-state-container {
    max-width: 1020px;
    margin: 71px auto 0;
  }
}
</style>
